import _get from 'lodash/get';

import { CmsComponents, Contentful } from 'src/lib/constants';
import { PageComponents } from 'src/lib/ts-types';

const contentTypeId = (component) => {
  return _get(component, Contentful.CONTENT_TYPE_ID);
};

const hasPaywallEntry = (component) => {
  return contentTypeId(component) === CmsComponents.PAYWALL;
};

const isEmbeddedVideoCard = (component) => {
  return contentTypeId(component) === CmsComponents.EMBEDDED_VIDEO_CARD;
};

const secretKeyOf = (entry: unknown) => {
  const secretKey = _get(entry, 'fields.secretKey');
  return typeof secretKey === 'string' && secretKey ? secretKey : '';
};

const isBackgroundContainer = (component) => {
  return contentTypeId(component) === CmsComponents.BACKGROUND_CONTAINER;
};

const childComponents = (component): PageComponents[] | null => {
  const children = component?.fields?.components;
  return Array.isArray(children) ? children : null;
};

const withChildComponents = (component, components: PageComponents[]) => {
  return {
    ...component,
    fields: {
      ...component.fields,
      components,
    },
  };
};

type PaywallSplit = {
  visible: PageComponents[];
  hidden: PageComponents[];
  paywall: PageComponents;
};

// A paywall inside a background container gates the rest of the page, not
// only the container's own following components. While the page is locked the
// container is split so the components before the gate keep its background.
// The unlocked page uses omitPaywallEntries instead, so the container stays
// whole and its gradient and spacing are not restarted.
const splitAtPaywall = (components: PageComponents[]): PaywallSplit | null => {
  if (!components?.length) {
    return null;
  }

  for (let index = 0; index < components.length; index += 1) {
    const component = components[index];

    if (hasPaywallEntry(component)) {
      return {
        visible: components.slice(0, index),
        hidden: components.slice(index + 1),
        paywall: component,
      };
    }

    const children = isBackgroundContainer(component)
      ? childComponents(component)
      : null;
    const nested = children ? splitAtPaywall(children) : null;
    if (!nested) {
      continue;
    }

    const before = components.slice(0, index);
    const after = components.slice(index + 1);

    return {
      visible:
        nested.visible.length > 0
          ? [...before, withChildComponents(component, nested.visible)]
          : before,
      hidden: [
        ...(nested.hidden.length > 0
          ? [withChildComponents(component, nested.hidden)]
          : []),
        ...after,
      ],
      paywall: nested.paywall,
    };
  }

  return null;
};

const forEachPaywallEntry = (
  components: PageComponents[] | null | undefined,
  visitEntry: (entry: PageComponents) => void,
) => {
  if (!components?.length) {
    return;
  }

  components.forEach((component) => {
    if (hasPaywallEntry(component)) {
      visitEntry(component);
    }

    if (isEmbeddedVideoCard(component)) {
      const gate = component?.fields?.gate;
      if (gate && typeof gate === 'object') {
        visitEntry(gate as PageComponents);
      }
    }

    if (isBackgroundContainer(component)) {
      forEachPaywallEntry(childComponents(component), visitEntry);
    }
  });
};

const secretKeysWhere = (
  components: PageComponents[],
  include: (entry: PageComponents) => boolean,
) => {
  const keys = new Set<string>();
  forEachPaywallEntry(components, (entry) => {
    if (!include(entry)) {
      return;
    }
    const secretKey = secretKeyOf(entry);
    if (secretKey) {
      keys.add(secretKey);
    }
  });
  return [...keys];
};

// Page paywalls and paywalls linked from an embedded video card. The video
// reference is not a page component, so the content split above ignores it,
// but Contentful settings still needs the key to offer Reset Paywall.
export const collectPaywallSecretKeys = (
  components: PageComponents[],
): string[] => {
  return secretKeysWhere(components, () => {
    return true;
  });
};

// Inverted gates remember an unlock by storing the secret, and remember a
// lock by storing "false". Reset has to delete those keys. Writing "false"
// would lock the video instead of returning it to the open default.
export const collectInvertedPaywallSecretKeys = (
  components: PageComponents[],
): string[] => {
  return secretKeysWhere(components, (entry) => {
    return entry?.fields?.invertLogic === true;
  });
};

// Drop paywall entries but keep their siblings inside the same container.
// Splitting the container for the unlocked page restarts its background.
export const omitPaywallEntries = (
  components: PageComponents[] = [],
): PageComponents[] => {
  let changed = false;
  const next = components.flatMap((component) => {
    if (hasPaywallEntry(component)) {
      changed = true;
      return [];
    }

    if (!isBackgroundContainer(component)) {
      return [component];
    }

    const children = childComponents(component);
    if (!children) {
      return [component];
    }

    const nextChildren = omitPaywallEntries(children);
    if (nextChildren === children) {
      return [component];
    }

    changed = true;
    if (!nextChildren.length) {
      return [];
    }

    return [withChildComponents(component, nextChildren)];
  });

  return changed ? next : components;
};

type PaywallHook = {
  hasPaywall: boolean;
  visibleComponents: PageComponents[];
  hiddenComponents: PageComponents[];
  paywallComponent: PageComponents;
};

// we split the array of components into two buckets
// when the editorial gate content entry is present
// one bucket of components we display and the other we do not
export const usePaywall = (components: PageComponents[]): PaywallHook => {
  const split = splitAtPaywall(components);

  if (!split) {
    return {
      hasPaywall: false,
      visibleComponents: components,
      hiddenComponents: [],
      paywallComponent: null,
    };
  }

  return {
    hasPaywall: true,
    visibleComponents: split.visible,
    hiddenComponents: split.hidden,
    paywallComponent: split.paywall,
  };
};
